import { useCallback } from 'react';
import { generatePath, matchPath, useHistory } from 'react-router-dom';
import { ActivityItemProps } from '../prefabs/AssetActivityItem';
import { useActivitySubject } from './useActivitySubject';
import {
  useActivityContext,
  useActivityContextWithPath
} from './useActivityContext';
import { isAlreadyVisiting } from '../utils/isAlreadyVisiting';
import { useCurrentUser } from '@cube3/state/src/redux/components/Administration/withCurrentUserAccount';
import { Share } from '@cube3/common/model/schema/resources/share';
import {
  DetailViewRouteParams,
  urlStructureDetailView,
  urlStructureShareRedirectSettings
} from '../../../routing/routingPaths';
import { getSharePath } from '../utils/getSharePath';
import { useResource__ALPHA } from '@cube3/state/src/redux/components/Hooks/useResource';

export const useActivityClickBehavior = (props: ActivityItemProps) => {
  const { activity, onClose } = props;
  const { subject, actor, related_resources: context } = activity.relationships;

  // Get the visiting asset
  const history = useHistory();
  const params = matchPath<DetailViewRouteParams>(
    location.pathname,
    urlStructureDetailView
  )?.params;
  const visitingAssetId = params?.assetId;
  const visitingAsset = useResource__ALPHA({
    resourceType: visitingAssetId ? 'asset' : undefined,
    resourceId: visitingAssetId
  });

  const {
    pathname: subjectPath,
    resource: subjectResource,
    thread: resolvedComment
  } = useActivitySubject(subject, actor?.id);

  const visiting = isAlreadyVisiting(history.location.pathname, subjectPath);
  const isTrashed = subjectResource && !!subjectResource.trashed_at;

  const { pathname: contextPathname } = useActivityContextWithPath(activity);

  const { share: shareFromContext } = useActivityContext(context);

  /** Open the asset detail view
   * - if it's a share event, will open the share settings
   */
  const visitObject = useCallback(
    (from?) => () => {
      if (!isTrashed) {
        history.push(
          subjectPath,
          from ? { from: history.location.pathname } : undefined
        );
      }
      onClose();
    },
    [isTrashed, subjectPath, onClose]
  );

  /** Open the parent folder/project of the object that was modified, for example an asset */
  const visitContext = useCallback(() => {
    if (!visiting) {
      history.push(contextPathname);
    }
    onClose();
  }, [visiting, contextPathname, onClose]);

  // #region share related activities
  // for the share activities, we want to display share details,
  // e.g.: `user commented on this asset in 'share'`,
  const [currentUserId] = useCurrentUser(true);

  const isShareLink = subject?.type === 'share';
  const share = (isShareLink ? subjectResource : shareFromContext) as Share;
  /** click the <share.display_name> will open the share settings modal, */
  const visitShareSettings = useCallback(() => {
    if (share && share.id && currentUserId === actor?.id) {
      history.push(
        generatePath(urlStructureShareRedirectSettings, {
          shareId: share.id
        })
      );
    }
  }, [share, currentUserId, actor]);

  const isComment = subject?.type === 'comment';
  const isResolvedAction = isComment && !!subjectResource?.resolution;

  /** In asset detail view, open the external share when clicking the comment/approval. */
  const visitReviewLink = useCallback(
    (toComment = false) =>
      () => {
        const path = share
          ? getSharePath(
              share,
              visitingAssetId,
              toComment &&
                isComment &&
                (isResolvedAction ? resolvedComment?.id : subject?.id)
            )
          : undefined;
        if (!share?.token || !path) return;
        window.open(path, '_blank', 'noreferrer noopener');
      },
    [share, visitingAssetId, isComment, isResolvedAction, resolvedComment]
  );

  return {
    visitingAsset: visitingAsset.resource,
    visitObject,
    visitContext,
    visitShareSettings,
    visitReviewLink
  };
};
